import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SearchFormType } from '../search-page-type.enum';
import { select, Store } from '@ngrx/store';
import { selectSeSearchItems } from '../redux/search-page.selectors';
import { Observable, Subscription, of, catchError, switchMap, take, finalize } from 'rxjs';
import { RootState } from '../../root-store/root.state';
import { ColDef } from 'ag-grid-community';
import {
    seSearchSafeExit,
    seSearchSetItems,
    seSearchToggleRightArea,
    seSearchTypeChange,
} from '../redux/search-page.actions';
import { Actions } from '@ngrx/effects';
import { SearchPageService } from '../search-page.service';
import { seSidePatientHistoryComponentNoResult } from '../../se-side/se-side-components/se-side-patient-history/redux/se-side-patient-history.actions';
import { TranslateService } from '@ngx-translate/core';
import { GridSelectionModel, SearchParams, SearchTypeInfo } from '../search-param.model';
import { SearchPageConfigurationService } from '../search-page-configuration.service';
import { seToolbarDisableAllButtons } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { SearchPageDiff } from '../../search-page/redux/search-page.state';
import { ModalDialogNotificationService, NotificationService } from '@se/se.ui.html5';

interface DoSearchParams {
    highlightRow: boolean;
    showDiff: boolean;
}

@Component({
    selector: 'ap-search-container',
    templateUrl: './ap-search-container.component.html',
    styleUrls: ['./ap-search-container.component.scss'],
})
export class ApSearchContainerComponent implements OnInit, OnDestroy {
    get disabledShowHideIdList(): string[] {
        // Lista degli id relativi alle colonne che non devono essere inseriti nel menu' Show\Hide
        return ['FeaAuthHead', 'FeaExamConsentHead', 'SendToWorklist', 'Doses', 'TicketOkHead'];
    }

    searchResult$: Observable<any[]>;
    disabledShowHideColList: ColDef[] = [];
    idPropertyName: string = 'id'; // DGS_AP: cablato ma da recuperare dalla DUMMY?
    disableSearch: boolean = false;
    actualSearchType: SearchTypeInfo;
    hideFilters: boolean = false;

    _actionSubscription: Subscription;
    _hideFiltersSubscription: Subscription;
    _hideRightAreaSubscription: Subscription;
    _searchTypeSubscription: Subscription;

    _formType: SearchFormType;
    _isBusy: boolean = false;

    @Input()
    set formType(value: SearchFormType) {
        this._formType = value;
        this.searchResult$ = this.store.pipe(select(selectSeSearchItems(value)));
    }

    @Input()
    canItemBeSelected;

    @Input()
    getButtonContentForSelection;

    @Input()
    rowSelection: GridSelectionModel = GridSelectionModel.single;

    @Input()
    canPatientHistoryOpenForSelection: Function = (actualSelectedItems) => {
        return actualSelectedItems && actualSelectedItems.length == 1; // By default si apre solo se c'è una sola selezione
    };

    @Input()
    searchOnInit: boolean = true;

    @Input()
    getItemDiff?: (item: any) => SearchPageDiff;

    @Input()
    set isBusy(value: boolean) {
        let delay = value ? 0 : 1000;
        setTimeout(() => {
            this._isBusy = value;
            if (this._isBusy) {
                this.store.dispatch(seToolbarDisableAllButtons());
            }
        }, delay);
    }

    get searchParams(): SearchParams {
        return this.searchPageService.getSearchParams(this.formType);
    }

    get searchTypes(): SearchTypeInfo[] {
        const types = this.searchPageConfigurationService.getSearchTypes(this.formType);
        if (!types || types.length < 1) {
            return [];
        }
        return types;
    }

    get isMultiSearchEnabled() {
        return !!this.searchTypes && this.searchTypes.length > 0;
    }

    get isBusy(): boolean {
        return this._isBusy;
    }
    get formType() {
        return this._formType;
    }

    constructor(
        //private searchPageConfigurationService: SearchPageConfigurationService,
        private searchPageService: SearchPageService,
        private searchPageConfigurationService: SearchPageConfigurationService,
        private notificationService: NotificationService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        private actions: Actions,
        private store: Store<RootState>,
        //private userConfigurationSettingsService: UserConfigurationSettingsService,
        //private logger: Logger,
    ) {}

    ngOnDestroy() {
        // this._actionSubscription.unsubscribe();
        if (this._hideFiltersSubscription) this._hideFiltersSubscription.unsubscribe();
        if (this._hideRightAreaSubscription) this._hideRightAreaSubscription.unsubscribe();
        if (this._searchTypeSubscription) this._searchTypeSubscription.unsubscribe();

        this.store.dispatch(seSearchSafeExit({ formType: this.formType }));
    }

    ngOnInit() {
        this.store.dispatch(seSearchTypeChange({ formType: this.formType }));

        // Prima leggo lo stato salvato degli slider e poi attacco i listener
        this.listenForStoreEvents();
        this.doSearch({
            highlightRow: false,
            showDiff: false,
        });
    }

    private doSearch({ highlightRow, showDiff }: DoSearchParams) {
        //this.isBusy = true;

        this.getResults().subscribe((newItems) => {
            this.store.dispatch(seSearchSetItems({ formType: this.formType, items: newItems }));

            // Comunico al componente side che la ricerca non ha prodotto risultati
            if (!newItems || newItems.length <= 0) {
                this.seSideFailedSearchOps('NoResults');
            }
        });
    }

    handleSearchRequired(info: any) {
        this.getResults().subscribe((newItems) => {
            this.store.dispatch(seSearchSetItems({ formType: this.formType, items: newItems }));

            // Comunico al componente side che la ricerca non ha prodotto risultati
            if (!newItems || newItems.length <= 0) {
                this.seSideFailedSearchOps('NoResults');
            }
        });
    }

    private listenForStoreEvents() {
        /* Cosa fa?
        this._actionSubscription = this.actions
            .pipe(
                ofType(seSearch, seSearchIfFiltersEq),
                filter((action) => {
                    if (action.formType !== this.formType) {
                        return false;
                    }
                    if ('filters' in action) {
                        return this.searchParamsMatch(action.filters);
                    }
                    return true;
                }),
            )
            .subscribe((action) => {
                this.doSearch({
                    highlightRow: action.highlightRow ?? false,
                    showDiff: action.showDiff ?? false,
                });
            });
            */

        /* Questa non c'è piu'semmai la dobbiamo legare al pannello dei filtri sopra
        this._hideFiltersSubscription = this.store
            .select('buttonsToolbar', 'leftSlider', 'checked')
            .subscribe((checked) => {
                this.store.dispatch(seSearchToggleLeftArea({ formType: this.formType, hide: !checked })); // Notifico lo stato
                this.hideFilters = !checked; // nascondo la toolbar
            });
        */

        this._hideRightAreaSubscription = this.store
            .select('buttonsToolbar', 'rightSlider', 'checked')
            .subscribe((checked) => {
                this.store.dispatch(seSearchToggleRightArea({ formType: this.formType, hide: checked })); // Notifico lo stato
            });

        this._searchTypeSubscription = this.store
            .select('searchPage', this.formType, 'actualSearchType')
            .subscribe((st) => (this.actualSearchType = st));
    }

    private searchParamsMatch(filters: Record<string, any>) {
        const searchParams = this.searchParams;

        for (const filterKey in filters) {
            if (
                Object.hasOwnProperty.call(filters, filterKey) &&
                !searchParams.matches(filterKey, filters[filterKey])
            ) {
                return false;
            }
        }
        return true;
    }

    private getResults() {
        this.isBusy = true;

        return this.store.pipe(
            select('searchPage', this.formType, 'actualSearchType'),
            take(1),
            switchMap((searchType) => {
                const searchInfo = {
                    ...this.searchParams.toJSON(),
                    searchType: searchType ? searchType.id : null,
                };

                return this.searchPageService.getResult(this.formType, searchInfo);
            }),
            catchError((e) => {
                // TooMuch??
                if (e?.error?.estensaFaultDetails?.typeName == 'TooMuchResultFault') {
                    try {
                        let messageKey = 'Exception_' + e.error.estensaFaultDetails.typeName;

                        let params = {};
                        let position = 0;
                        for (let param in e.error.estensaFaultDetails.estensaFault) {
                            if (param === 'errorId' || param === 'message' || param === 'completeMessage') {
                                continue;
                            }
                            params['p' + position] = e.error.estensaFaultDetails.estensaFault[param];
                            position++;
                        }

                        this.modalDialogNotificationService.warning(this.translate.instant(messageKey, params));
                    } catch {
                        this.notificationService.error(
                            e.message ? e.message : '',
                            this.translate.instant('ErrorLoadingData'),
                        );
                    }
                } else
                    this.notificationService.error(
                        e.message ? e.message : '',
                        this.translate.instant('ErrorLoadingData'),
                    );

                // SeSide
                this.seSideFailedSearchOps('ErrorLoadingData');

                return of([]);
            }),
            finalize(() => {
                this.isBusy = false;
            }),
        );
    }

    private seSideFailedSearchOps(message: string) {
        // Patient History
        this.store.dispatch(
            seSidePatientHistoryComponentNoResult({
                formType: this.formType,
                message: this.translate.instant(message),
            }),
        );
    }

    fnCanItemBeSelectedHandler = (itemToSelect, actualSelectedItems) => {
        if (this.canItemBeSelected) {
            return this.canItemBeSelected(itemToSelect, actualSelectedItems);
        }

        return true;
    };

    fnCanPatientHistoryOpenForSelectionHandler = (actualSelectedItems) => {
        if (this.canPatientHistoryOpenForSelection) {
            return this.canPatientHistoryOpenForSelection(actualSelectedItems);
        }
        return false;
    };

    fnGetButtonContentForSelectionHandler = (rowData) => {
        if (this.getButtonContentForSelection) {
            return this.getButtonContentForSelection(rowData);
        }

        return null;
    };

    handlePrimaryChange = ({ colId, primary }: { colId: string; primary: boolean }) => {
        this.searchPageConfigurationService.setColDefPrimary(this.formType, colId, primary);
    };

    handleVisibilityChange = ({ colId, isVisible }: { colId: string; isVisible: boolean }) => {
        this.searchPageConfigurationService.setColDefVisibility(this.formType, colId, isVisible);
    };
}
